import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { api } from "@/lib/api";
import GameWindowBanner from "@/components/GameWindowBanner";
import { toast } from "sonner";
import { Grid3x3, ArrowLeft, Trophy, Award, CheckCircle2, XCircle, Clock } from "lucide-react";
import { applyTheme } from "@/lib/themes";

function Timer({ running, onTick }) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);
  useEffect(() => { if (onTick) onTick(sec); }, [sec, onTick]);
  const m = Math.floor(sec/60), s = sec%60;
  return <div className="font-mono-bq text-2xl font-black">{String(m).padStart(2,"0")}:{String(s).padStart(2,"0")}</div>;
}

export default function SudokuPage() {
  const token = useToken();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [puzzle, setPuzzle] = useState(null);
  const [grid, setGrid] = useState(null);
  const [given, setGiven] = useState(null);
  const [selected, setSelected] = useState({ r: -1, c: -1 });
  const [duration, setDuration] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [meR, sR] = await Promise.all([
          api.get(`/employee/me?token=${token}`),
          api.get(`/sudoku/today?token=${token}`),
        ]);
        setMe(meR.data); applyTheme(meR.data.theme || "standard");
        setPuzzle(sR.data);
        setGrid(sR.data.puzzle.map((row) => [...row]));
        setGiven(sR.data.puzzle.map((row) => row.map((c) => c !== 0)));
        setSubmitted(sR.data.submitted);
        if (sR.data.submitted) setResult({ correct: sR.data.submitted_correct, score: sR.data.submitted_score, duration: sR.data.submitted_duration });
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load");
      } finally { setLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    api.get(`/sudoku/leaderboard?token=${token}&period=${period}`).then(r => setLeaderboard(r.data)).catch(()=>{});
  }, [period, token, submitted]);

  const setCell = useCallback((val) => {
    if (selected.r < 0 || submitted) return;
    if (given[selected.r][selected.c]) return;
    if (val > grid.length) return;   // ignore digits outside the grid size (e.g. 7-9 on a 6x6)
    const ng = grid.map((row) => [...row]);
    ng[selected.r][selected.c] = val;
    setGrid(ng);
  }, [selected, submitted, given, grid]);

  const handleKey = useCallback((e) => {
    if (e.key >= "1" && e.key <= "9") setCell(parseInt(e.key, 10));
    else if (e.key === "Backspace" || e.key === "Delete" || e.key === "0") setCell(0);
  }, [setCell]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const submit = async () => {
    if (submittingRef.current || submitted) return;   // synchronous guard: no concurrent/duplicate submits
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const r = await api.post(`/sudoku/submit?token=${token}`, { grid, duration_sec: duration });
      setResult(r.data); setSubmitted(true);
      if (r.data.correct) toast.success(`Solved! +${r.data.score} points`);
      else toast.error(`${r.data.cells_correct}/${r.data.total || (grid.length * grid.length)} cells correct.`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400 && typeof detail === "string" && detail.toLowerCase().includes("already submitted")) {
        // Already recorded (e.g. from an earlier tap) — reflect it instead of erroring.
        setSubmitted(true);
        toast.message("You've already submitted today's Sudoku.");
      } else if (status === 429) {
        toast.error("Please wait a moment before submitting again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Submit failed");
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-black text-2xl">Loading…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center"><div className="brutal-card p-8 text-center"><XCircle className="mx-auto" size={48} style={{color: "var(--bq-danger)"}}/><h1 className="text-2xl font-black mt-3">{error}</h1></div></div>;

  const N = grid.length;
  const boxR = N === 6 ? 2 : 3;   // box height (6x6 -> 2, 9x9 -> 3)
  const boxC = 3;                 // box width (3 for both 6x6 and 9x9)

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex justify-between items-center themed-border" style={{background: "var(--bq-header-bg)"}}>
        <div className="flex items-center gap-3">
          <button onClick={()=>navigate(-1)} data-testid="back-to-hub" className="p-2 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}><ArrowLeft size={18}/></button>
          <div className="w-10 h-10 flex items-center justify-center" style={{background: "var(--bq-text)"}}><Grid3x3 size={22} style={{color: "var(--bq-accent)"}}/></div>
          <div><div className="font-black uppercase">Sudoku Daily</div><div className="text-xs font-mono-bq" style={{color: "var(--bq-muted)"}}>{puzzle?.date}</div></div>
        </div>
        <div className="flex items-center gap-2">
          <Timer running={!submitted} onTick={setDuration}/>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3"><GameWindowBanner game="sudoku" /></div>
        <div className="lg:col-span-2">
          <div className="brutal-card p-4">
            <div className="mx-auto" style={{display: "grid", gridTemplateColumns: `repeat(${N}, 1fr)`, width: "min(90vw, 540px)", aspectRatio: "1"}}>
              {grid.map((row, r) => row.map((val, c) => {
                const isSel = selected.r === r && selected.c === c;
                const isSameRow = selected.r === r;
                const isSameCol = selected.c === c;
                const isSameBox = Math.floor(selected.r/boxR) === Math.floor(r/boxR) && Math.floor(selected.c/boxC) === Math.floor(c/boxC);
                const borderR = (c % boxC === boxC - 1) && c !== N - 1 ? "3px" : "1px";
                const borderB = (r % boxR === boxR - 1) && r !== N - 1 ? "3px" : "1px";
                const isGiven = given[r][c];
                return (
                  <button
                    key={`${r}-${c}`}
                    data-testid={`sudoku-cell-${r}-${c}`}
                    onClick={() => setSelected({ r, c })}
                    className="flex items-center justify-center font-black text-lg md:text-2xl transition-colors"
                    style={{
                      borderRight: `${borderR} solid var(--bq-text)`,
                      borderBottom: `${borderB} solid var(--bq-text)`,
                      borderTop: r === 0 ? "3px solid var(--bq-text)" : undefined,
                      borderLeft: c === 0 ? "3px solid var(--bq-text)" : undefined,
                      background: isSel ? "var(--bq-accent)" : isSameRow || isSameCol || isSameBox ? "rgba(0,0,0,0.04)" : "var(--bq-card-bg)",
                      color: isGiven ? "var(--bq-text)" : "var(--bq-danger)",
                      cursor: submitted ? "default" : "pointer",
                    }}
                  >
                    {val !== 0 ? val : ""}
                  </button>
                );
              }))}
            </div>
            <div className="grid gap-1 mt-4 max-w-md mx-auto" style={{gridTemplateColumns: `repeat(${N}, 1fr)`}}>
              {Array.from({length: N}, (_, i) => i + 1).map((n) => (
                <button key={n} data-testid={`num-${n}`} disabled={submitted} onClick={()=>setCell(n)} className="p-2 border-2 themed-border font-black text-lg" style={{background: "var(--bq-card-bg)"}}>{n}</button>
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <button disabled={submitted} onClick={()=>setCell(0)} className="brutal-btn-secondary">Clear cell</button>
              {!submitted ? (
                <button data-testid="sudoku-submit" onClick={submit} disabled={submitting} className="brutal-btn disabled:opacity-60">{submitting ? "Submitting…" : "Submit"}</button>
              ) : (
                <div className="px-4 py-2 border-2 themed-border font-black uppercase" style={{background: result?.correct ? "var(--bq-accent)" : "var(--bq-danger)", color: result?.correct ? "var(--bq-text)" : "white"}}>
                  {result?.correct ? `Solved! +${result.score}` : `${result?.cells_correct || 0}/${result?.total || (grid.length * grid.length)}`}
                </div>
              )}
            </div>
          </div>
        </div>

        <aside>
          <div className="brutal-card p-5 sticky top-4">
            <h3 className="text-xl font-black uppercase flex items-center gap-2 mb-3"><Trophy size={20}/> Sudoku Board</h3>
            <div className="flex gap-1 mb-3 border-2 themed-border p-1">
              {["daily","weekly","monthly","all"].map((p) => (
                <button key={p} onClick={()=>setPeriod(p)} className="flex-1 text-xs font-black uppercase py-1" style={{background: period===p ? "var(--bq-text)" : "var(--bq-card-bg)", color: period===p ? "var(--bq-bg)" : "var(--bq-text)"}}>{p}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {leaderboard.length === 0 && <div className="text-sm text-center py-4" style={{color: "var(--bq-muted)"}}>No data yet.</div>}
              {leaderboard.map((r, i) => (
                <div key={r.employee_id} className="flex items-center justify-between p-2 border-2 themed-border" style={{background: r.employee_id === me?.employee_id ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center font-black text-xs" style={{background: i < 3 ? "var(--bq-accent2)" : "var(--bq-text)", color: i < 3 ? "#000" : "var(--bq-bg)"}}>{i+1}</div>
                    <div className="font-bold text-sm">{r.name}</div>
                  </div>
                  <div className="text-right"><div className="font-mono-bq font-black">{r.score}</div><div className="text-xs" style={{color: "var(--bq-muted)"}}>{r.solves} solves</div></div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
